import { useContext } from "react"
import { gql, useQuery } from "@apollo/client"

import { UserContext } from "../context/userContext"
import { LoaderMedium } from "./Loaders"
import styles from "../styles/AllParticipantsSummary.module.css"

// Enterprise-wide staff snapshot used to build sidebar summary counts.
const GET_ENT_STAFF_SUMMARY = gql`
  query GetUsersByEnterpriseIdSummary($staff_id: ID!, $enterprise_id: ID!) {
    users: getUsersByEnterpriseId(
      staff_id: $staff_id
      enterprise_id: $enterprise_id
    ) {
      staff_user_status
      assigned_participants
    }
  }
`

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export default function EntStaffSummary() {
  const userCtx = useContext(UserContext)

  const { data, loading, error } = useQuery(GET_ENT_STAFF_SUMMARY, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      enterprise_id: userCtx.session.user?.enterprise_id,
    },
    skip: !userCtx?.session?.user?.staff_id || !userCtx?.session?.user?.enterprise_id,
    fetchPolicy: "network-only",
  })

  if (loading) {
    return (
      <div className={styles.card}>
        <LoaderMedium />
      </div>
    )
  }

  if (error) {
    return <p>Error loading enterprise staff stats: {error.message}</p>
  }

  const staffRows = data?.users || []

  // Derived values shown in the summary box.
  const totalStaff = staffRows.length
  const activeStaff = staffRows.filter(
    (staff) => `${staff?.staff_user_status || ""}`.toUpperCase() === "ACTIVE",
  ).length
  const inactiveStaff = staffRows.filter(
    (staff) => `${staff?.staff_user_status || ""}`.toUpperCase() === "INACTIVE",
  ).length
  const staffWithAssignedParticipants = staffRows.filter(
    (staff) => toNumber(staff?.assigned_participants) > 0,
  ).length

  const rows = [
    { label: "Total Staff", value: totalStaff },
    { label: "Active", value: activeStaff },
    { label: "Inactive", value: inactiveStaff },
    {
      label: "Staff w/ assigned participants",
      value: staffWithAssignedParticipants,
    },
  ]

  return (
    <section className={styles.card}>
      <div className={styles.headerRow}>
        <h3 className="sidebar-section-title">Enterprise Staff</h3>
      </div>

      <div className={styles.body}>
        {rows.map((row) => (
          <div key={row.label} className={styles.infoLine}>
            <span className={styles.lineLabel}>{row.label}</span>
            <span className={styles.countPill}>{row.value}</span>
          </div>
        ))}
      </div>
    </section>
  )
}
